import React, { useEffect, useState } from 'react';
import { Settings, Save, Mail, Bell, CheckCircle2, AlertCircle, AlertTriangle, Send, Eye, EyeOff, ExternalLink, Languages, Hammer, Facebook, Instagram, Youtube, Linkedin } from 'lucide-react';
import { api, formatApiError } from '../../lib/api';

export default function AdminSettingsPage() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    brevo_api_key: '',
    brevo_sender_email: '',
    brevo_sender_name: 'CMIC',
    notifications_enabled: true,
    show_passive_languages: false,
    home_members_mode: 'manual',
    home_members_count: 80,
    home_years_mode: 'auto',
    home_years_count: 44,
    home_founding_year: 1982,
    email_test_mode: true,
    email_test_inbox: 'cmicpagina@gmail.com',
    email_test_inbox_name: 'CMIC (modo prueba)',
    maintenance_mode: false,
    social_facebook: '',
    social_instagram: '',
    social_youtube: '',
    social_linkedin: '',
    max_specialties: 0,
  });
  const [showKey, setShowKey] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [feedback, setFeedback] = useState(null); // {type:'success'|'error', msg:''}

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/settings');
      setSettings(data);
      setForm({
        brevo_api_key: '',
        brevo_sender_email: data.brevo_sender_email || '',
        brevo_sender_name: data.brevo_sender_name || 'CMIC',
        notifications_enabled: data.notifications_enabled !== false,
        show_passive_languages: !!data.show_passive_languages,
        home_members_mode: data.home_members_mode || 'manual',
        home_members_count: Number(data.home_members_count ?? 80),
        home_years_mode: data.home_years_mode || 'auto',
        home_years_count: Number(data.home_years_count ?? 44),
        home_founding_year: Number(data.home_founding_year ?? 1982),
        email_test_mode: data.email_test_mode !== false,
        email_test_inbox: data.email_test_inbox || 'cmicpagina@gmail.com',
        email_test_inbox_name: data.email_test_inbox_name || 'CMIC (modo prueba)',
        maintenance_mode: !!data.maintenance_mode,
        social_facebook: data.social_facebook || '',
        social_instagram: data.social_instagram || '',
        social_youtube: data.social_youtube || '',
        social_linkedin: data.social_linkedin || '',
        max_specialties: Number(data.max_specialties ?? 0),
      });
    } catch (err) { setFeedback({ type: 'error', msg: formatApiError(err.response?.data?.detail) }); }
    setLoading(false);
  }
  useEffect(() => { load(); }, []);

  async function save() {
    setSaving(true); setFeedback(null);
    try {
      // Only send api_key if user actually typed one
      const payload = {
        brevo_sender_email: form.brevo_sender_email,
        brevo_sender_name: form.brevo_sender_name,
        notifications_enabled: form.notifications_enabled,
        show_passive_languages: form.show_passive_languages,
        home_members_mode: form.home_members_mode,
        home_members_count: form.home_members_count,
        home_years_mode: form.home_years_mode,
        home_years_count: form.home_years_count,
        home_founding_year: form.home_founding_year,
        email_test_mode: form.email_test_mode,
        email_test_inbox: form.email_test_inbox,
        email_test_inbox_name: form.email_test_inbox_name,
        maintenance_mode: form.maintenance_mode,
        social_facebook: form.social_facebook,
        social_instagram: form.social_instagram,
        social_youtube: form.social_youtube,
        social_linkedin: form.social_linkedin,
        max_specialties: form.max_specialties,
      };
      if (form.brevo_api_key && form.brevo_api_key.trim()) payload.brevo_api_key = form.brevo_api_key.trim();
      const { data } = await api.put('/admin/settings', payload);
      setSettings(data);
      setForm((p) => ({ ...p, brevo_api_key: '' }));
      setFeedback({ type: 'success', msg: 'Configuración guardada correctamente.' });
    } catch (err) { setFeedback({ type: 'error', msg: formatApiError(err.response?.data?.detail) }); }
    setSaving(false);
  }

  async function testConnection() {
    setTesting(true); setFeedback(null);
    try {
      const { data } = await api.post('/admin/settings/test-brevo');
      setFeedback({ type: 'success', msg: `✓ Conexión válida. Cuenta Brevo: ${data.email}` });
      await load();
    } catch (err) { setFeedback({ type: 'error', msg: formatApiError(err.response?.data?.detail) }); }
    setTesting(false);
  }

  async function sendTestEmail() {
    if (!testEmail.trim()) return;
    setSendingTest(true); setFeedback(null);
    try {
      await api.post('/admin/settings/send-test-email', { to: testEmail.trim() });
      setFeedback({ type: 'success', msg: `✉ Correo de prueba enviado a ${testEmail}` });
    } catch (err) { setFeedback({ type: 'error', msg: formatApiError(err.response?.data?.detail) }); }
    setSendingTest(false);
  }

  if (loading) return <div className="flex justify-center py-16"><div className="w-8 h-8 border-3 border-cmic-200 border-t-cmic-500 rounded-full animate-spin" /></div>;

  return (
    <div data-testid="admin-settings-page" className="max-w-3xl space-y-6">
      <header>
        <h1 className="text-2xl font-bold text-charcoal-800 flex items-center gap-2"><Settings size={22} />Configuración</h1>
        <p className="text-charcoal-500 text-sm mt-1">Integraciones y comportamiento del sistema.</p>
      </header>

      {feedback && (
        <div data-testid="settings-feedback" className={`rounded-xl p-4 text-sm flex items-start gap-2 ${feedback.type === 'success' ? 'bg-emerald-50 border border-emerald-200 text-emerald-800' : 'bg-rose-50 border border-rose-200 text-rose-800'}`}>
          {feedback.type === 'success' ? <CheckCircle2 size={16} className="mt-0.5 flex-shrink-0" /> : <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />}
          <span>{feedback.msg}</span>
        </div>
      )}

      {/* Demo / construction mode + social links */}
      <div className="bg-white border border-gray-200 rounded-2xl p-6 space-y-5" data-testid="settings-demo-card">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center flex-shrink-0"><Hammer size={18} /></div>
          <div className="flex-1">
            <h2 className="font-semibold text-charcoal-800">Modo demo / página en construcción</h2>
            <p className="text-xs text-charcoal-500 mt-0.5">
              Al activarlo, el público ve una página de "en construcción". Solo admin y consejo navegan el sitio completo; los miembros solo pueden entrar a revisar y editar su propio perfil.
            </p>
          </div>
        </div>
        <label className={`flex items-center justify-between gap-3 rounded-xl px-4 py-3 border cursor-pointer ${form.maintenance_mode ? 'bg-amber-50 border-amber-200' : 'bg-gray-50 border-gray-200'}`}>
          <span className="text-sm font-medium text-charcoal-700">
            {form.maintenance_mode ? 'Modo demo ACTIVADO (sitio en construcción para el público)' : 'Modo demo desactivado (sitio público normal)'}
          </span>
          <span
            role="switch" aria-checked={form.maintenance_mode}
            onClick={() => setForm({ ...form, maintenance_mode: !form.maintenance_mode })}
            data-testid="settings-maintenance-toggle"
            className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors flex-shrink-0 ${form.maintenance_mode ? 'bg-amber-500' : 'bg-charcoal-300'}`}
          >
            <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${form.maintenance_mode ? 'translate-x-6' : 'translate-x-1'}`} />
          </span>
        </label>

        <div className="border-t border-gray-100 pt-4 space-y-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-charcoal-500">Redes sociales del CMIC (footer)</p>
          {[
            { key: 'social_facebook', label: 'Facebook', Icon: Facebook, ph: 'https://facebook.com/...' },
            { key: 'social_instagram', label: 'Instagram', Icon: Instagram, ph: 'https://instagram.com/...' },
            { key: 'social_youtube', label: 'YouTube', Icon: Youtube, ph: 'https://youtube.com/@...' },
            { key: 'social_linkedin', label: 'LinkedIn', Icon: Linkedin, ph: 'https://linkedin.com/company/...' },
          ].map(({ key, label, Icon, ph }) => (
            <div key={key} className="flex items-center gap-3">
              <Icon size={18} className="text-charcoal-400 flex-shrink-0" />
              <input
                type="url" value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                placeholder={ph} data-testid={`settings-${key}`}
                className="flex-1 rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cmic-500/20 focus:border-cmic-500"
              />
            </div>
          ))}
        </div>

        <button onClick={save} disabled={saving} data-testid="settings-demo-save" className="inline-flex items-center gap-2 bg-cmic-600 text-white rounded-lg px-4 py-2 text-sm font-medium hover:bg-cmic-700 disabled:opacity-50">
          <Save size={15} /> Guardar
        </button>
      </div>

      {/* Brevo */}
      <div className="bg-white border border-gray-200 rounded-2xl p-6 space-y-4" data-testid="settings-brevo-card">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center flex-shrink-0"><Mail size={18} /></div>
          <div className="flex-1">
            <h2 className="font-semibold text-charcoal-800">Brevo (correos transaccionales)</h2>
            <p className="text-xs text-charcoal-500 mt-0.5">
              Configura tu API key para enviar notificaciones por email cuando se aprueben/rechacen cambios de perfil.{' '}
              <a href="https://app.brevo.com/settings/keys/api" target="_blank" rel="noreferrer" className="text-cmic-600 hover:text-cmic-700 inline-flex items-center gap-0.5">
                Obtener API key <ExternalLink size={10} />
              </a>
            </p>
          </div>
          {settings?.brevo_api_key_set && (
            <span className="inline-flex items-center gap-1 text-[10px] font-bold bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">
              <CheckCircle2 size={10} />Configurado
            </span>
          )}
        </div>

        <div>
          <label className="block text-xs font-medium text-charcoal-600 mb-1.5">
            API Key {settings?.brevo_api_key_set && <span className="text-charcoal-400">· Actual: {settings.brevo_api_key_preview}</span>}
          </label>
          <div className="relative">
            <input
              type={showKey ? 'text' : 'password'}
              value={form.brevo_api_key}
              onChange={(e) => setForm({ ...form, brevo_api_key: e.target.value })}
              placeholder={settings?.brevo_api_key_set ? 'Deja vacío para mantener la actual' : 'xkeysib-...'}
              data-testid="settings-brevo-api-key"
              className="w-full rounded-xl border border-gray-200 pl-4 pr-10 py-2.5 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-cmic-500/30 focus:border-cmic-500"
            />
            <button onClick={() => setShowKey((p) => !p)} className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-charcoal-400 hover:text-charcoal-600">
              {showKey ? <EyeOff size={14} /> : <Eye size={14} />}
            </button>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-charcoal-600 mb-1.5">Email del remitente (verificado en Brevo)</label>
            <input
              type="email"
              value={form.brevo_sender_email}
              onChange={(e) => setForm({ ...form, brevo_sender_email: e.target.value })}
              placeholder="no-reply@cmic.org.mx"
              data-testid="settings-brevo-sender-email"
              className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-cmic-500/30 focus:border-cmic-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-charcoal-600 mb-1.5">Nombre del remitente</label>
            <input
              type="text"
              value={form.brevo_sender_name}
              onChange={(e) => setForm({ ...form, brevo_sender_name: e.target.value })}
              placeholder="CMIC"
              data-testid="settings-brevo-sender-name"
              className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-cmic-500/30 focus:border-cmic-500"
            />
          </div>
        </div>

        <div className="flex flex-wrap gap-2 pt-2">
          <button onClick={save} disabled={saving} data-testid="settings-save-btn" className="inline-flex items-center gap-2 bg-cmic-600 text-white rounded-lg px-4 py-2 text-sm font-medium hover:bg-cmic-700 disabled:opacity-50">
            <Save size={14} />{saving ? 'Guardando…' : 'Guardar'}
          </button>
          {settings?.brevo_api_key_set && (
            <button onClick={testConnection} disabled={testing} data-testid="settings-test-btn" className="inline-flex items-center gap-2 bg-white border border-gray-200 text-charcoal-700 rounded-lg px-4 py-2 text-sm font-medium hover:bg-gray-50 disabled:opacity-50">
              {testing ? <div className="w-3.5 h-3.5 border-2 border-charcoal-300 border-t-charcoal-600 rounded-full animate-spin" /> : <CheckCircle2 size={14} />}Probar conexión
            </button>
          )}
        </div>

        {settings?.brevo_api_key_set && (
          <div className="border-t border-gray-100 pt-4 mt-4">
            <label className="block text-xs font-medium text-charcoal-600 mb-1.5">Enviar correo de prueba a:</label>
            <div className="flex gap-2">
              <input
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="tu-email@ejemplo.com"
                data-testid="settings-test-email-input"
                className="flex-1 rounded-xl border border-gray-200 px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cmic-500/30 focus:border-cmic-500"
              />
              <button onClick={sendTestEmail} disabled={sendingTest || !testEmail.trim()} data-testid="settings-send-test-btn" className="inline-flex items-center gap-2 bg-cmic-50 text-cmic-700 rounded-xl px-4 py-2 text-sm font-medium hover:bg-cmic-100 disabled:opacity-50">
                {sendingTest ? <div className="w-3.5 h-3.5 border-2 border-cmic-300 border-t-cmic-600 rounded-full animate-spin" /> : <Send size={14} />}Enviar
              </button>
            </div>
            {settings.brevo_account_email && <p className="text-[11px] text-charcoal-500 mt-2">Cuenta validada: {settings.brevo_account_email} · {settings.last_validated_at && new Date(settings.last_validated_at).toLocaleString('es-MX')}</p>}
          </div>
        )}
      </div>

      {/* Notifications */}
      <div className="bg-white border border-gray-200 rounded-2xl p-6" data-testid="settings-notifications-card">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-cmic-50 text-cmic-600 flex items-center justify-center flex-shrink-0"><Bell size={18} /></div>
          <div className="flex-1">
            <h2 className="font-semibold text-charcoal-800">Notificaciones por email</h2>
            <p className="text-xs text-charcoal-500 mt-0.5">Envía un correo automático al miembro cuando se apruebe o rechace su solicitud de edición de perfil.</p>
          </div>
        </div>
        <label className="inline-flex items-center gap-3 cursor-pointer">
          <div className="relative">
            <input
              type="checkbox"
              checked={form.notifications_enabled}
              onChange={(e) => setForm({ ...form, notifications_enabled: e.target.checked })}
              data-testid="settings-notifications-toggle"
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 rounded-full peer-checked:bg-cmic-500 transition-colors" />
            <div className="absolute left-0.5 top-0.5 w-5 h-5 bg-white rounded-full transition-transform peer-checked:translate-x-5 shadow" />
          </div>
          <span className="text-sm text-charcoal-700">{form.notifications_enabled ? 'Activadas' : 'Desactivadas'}</span>
        </label>
      </div>

      {/* Passive languages toggle */}
      <div className="bg-white border border-gray-100 rounded-2xl shadow-sm p-6">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-cmic-50 flex items-center justify-center flex-shrink-0">
            <Languages size={18} className="text-cmic-600" />
          </div>
          <div className="flex-1">
            <h2 className="font-semibold text-charcoal-800">Mostrar idiomas pasivos en el directorio</h2>
            <p className="text-xs text-charcoal-500 mt-0.5">
              Si está desactivado, los idiomas pasivos de TODOS los intérpretes se ocultan en las cards del directorio y en cada perfil público.
              Los idiomas activos siempre se muestran.
            </p>
          </div>
        </div>
        <label className="inline-flex items-center gap-3 cursor-pointer">
          <div className="relative">
            <input
              type="checkbox"
              checked={form.show_passive_languages}
              onChange={(e) => setForm({ ...form, show_passive_languages: e.target.checked })}
              data-testid="settings-show-passive-toggle"
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 rounded-full peer-checked:bg-cmic-500 transition-colors" />
            <div className="absolute left-0.5 top-0.5 w-5 h-5 bg-white rounded-full transition-transform peer-checked:translate-x-5 shadow" />
          </div>
          <span className="text-sm text-charcoal-700">{form.show_passive_languages ? 'Visibles' : 'Ocultos (recomendado)'}</span>
        </label>
      </div>

      {/* Max specialties per interpreter */}
      <div className="bg-white border border-gray-200 rounded-2xl p-6" data-testid="settings-max-specialties-card">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-cmic-50 flex items-center justify-center flex-shrink-0">
            <Languages size={18} className="text-cmic-600" />
          </div>
          <div className="flex-1">
            <h2 className="font-semibold text-charcoal-800">Máximo de especialidades por intérprete</h2>
            <p className="text-xs text-charcoal-500 mt-0.5">
              Limita cuántas especialidades puede registrar cada intérprete en su perfil. Usa <strong>0</strong> para no poner límite.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <input
            type="number"
            min={0}
            value={form.max_specialties}
            onChange={(e) => setForm({ ...form, max_specialties: Math.max(0, Number(e.target.value) || 0) })}
            data-testid="settings-max-specialties"
            className="w-28 rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cmic-500/30"
          />
          <span className="text-sm text-charcoal-500">{Number(form.max_specialties) > 0 ? `máx. ${form.max_specialties} especialidades` : 'Sin límite'}</span>
          <button onClick={save} disabled={saving} data-testid="settings-max-specialties-save"
            className="ml-auto inline-flex items-center gap-2 bg-cmic-600 text-white rounded-lg px-4 py-2 text-sm font-medium hover:bg-cmic-700 disabled:opacity-50">
            <Save size={14} />{saving ? 'Guardando…' : 'Guardar'}
          </button>
        </div>
      </div>
      {/* Email TEST/PROD mode toggle — visible AT THE TOP because it's
          critical to avoid sending real emails to members by mistake. */}
      <div
        className={`rounded-2xl border-2 shadow-sm p-6 ${form.email_test_mode ? 'bg-amber-50/70 border-amber-300' : 'bg-emerald-50/40 border-emerald-300'}`}
        data-testid="settings-email-mode-card"
      >
        <div className="flex items-start gap-3 mb-4">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${form.email_test_mode ? 'bg-amber-100' : 'bg-emerald-100'}`}>
            {form.email_test_mode ? <AlertTriangle size={18} className="text-amber-700" /> : <Send size={18} className="text-emerald-700" />}
          </div>
          <div className="flex-1">
            <h2 className="font-semibold text-charcoal-800">
              Modo de envío de correos: {' '}
              <span className={form.email_test_mode ? 'text-amber-700' : 'text-emerald-700'}>
                {form.email_test_mode ? 'PRUEBA' : 'PRODUCCIÓN'}
              </span>
            </h2>
            <p className="text-xs text-charcoal-600 mt-1">
              {form.email_test_mode
                ? `Todos los correos del sistema se redirigen a una sola casilla (${form.email_test_inbox}) con un banner amarillo. Nadie del consejo ni intérpretes recibirán correos reales. Ideal mientras pruebas.`
                : 'Los correos del sistema se envían a sus destinatarios REALES (intérpretes, miembros del consejo, clientes). Úsalo solo cuando estés 100% seguro.'}
            </p>
          </div>
        </div>

        <label className="flex items-center gap-3 cursor-pointer select-none mb-3">
          <button
            type="button"
            role="switch"
            aria-checked={form.email_test_mode}
            onClick={() => setForm({ ...form, email_test_mode: !form.email_test_mode })}
            data-testid="settings-email-test-toggle"
            className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${form.email_test_mode ? 'bg-amber-500' : 'bg-emerald-500'}`}
          >
            <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${form.email_test_mode ? 'translate-x-1' : 'translate-x-6'}`} />
          </button>
          <span className="text-sm text-charcoal-700">
            {form.email_test_mode ? 'Activado (prueba) — alterna a producción cuando estés listo' : 'Desactivado (producción) — los correos van a los destinatarios reales'}
          </span>
        </label>

        <div className="grid sm:grid-cols-2 gap-3 mt-3">
          <div>
            <label className="block text-[11px] text-charcoal-500 mb-1">Casilla de prueba (a dónde se redirigen)</label>
            <input
              type="email"
              value={form.email_test_inbox}
              onChange={(e) => setForm({ ...form, email_test_inbox: e.target.value })}
              disabled={!form.email_test_mode}
              data-testid="settings-email-test-inbox"
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 disabled:opacity-50 disabled:bg-gray-50"
              placeholder="cmicpagina@gmail.com"
            />
          </div>
          <div>
            <label className="block text-[11px] text-charcoal-500 mb-1">Nombre de la casilla</label>
            <input
              type="text"
              value={form.email_test_inbox_name}
              onChange={(e) => setForm({ ...form, email_test_inbox_name: e.target.value })}
              disabled={!form.email_test_mode}
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 disabled:opacity-50 disabled:bg-gray-50"
              placeholder="CMIC (modo prueba)"
            />
          </div>
        </div>

        {!form.email_test_mode && (
          <div className="mt-3 bg-red-50 border border-red-300 rounded-lg p-3 flex items-start gap-2" data-testid="settings-email-prod-warning">
            <AlertTriangle size={16} className="text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-red-900">
              <strong>Modo PRODUCCIÓN activo.</strong> Los correos llegarán a los miembros, clientes y otros destinatarios reales.
              Verifica que las plantillas (newsletter, invitaciones, contact form) sean correctas antes de enviar.
            </p>
          </div>
        )}

        <div className="mt-4">
          <button
            onClick={save}
            disabled={saving}
            data-testid="settings-email-mode-save-btn"
            className="inline-flex items-center gap-2 bg-cmic-600 text-white rounded-lg px-4 py-2 text-sm font-medium hover:bg-cmic-700 disabled:opacity-50"
          >
            <Save size={14} />{saving ? 'Guardando…' : 'Guardar modo de envío'}
          </button>
        </div>
      </div>

      {/* Home stats banner */}
      <div className="bg-white border border-gray-100 rounded-2xl shadow-sm p-6" data-testid="settings-home-stats-card">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center flex-shrink-0">
            <Settings size={18} className="text-amber-600" />
          </div>
          <div className="flex-1">
            <h2 className="font-semibold text-charcoal-800">Estadísticas de la página de inicio</h2>
            <p className="text-xs text-charcoal-500 mt-0.5">
              Controla los dos números grandes que aparecen bajo el hero: miembros activos y años de trayectoria.
              Puedes calcularlos automáticamente o fijar un número manual.
            </p>
          </div>
        </div>

        {/* Miembros activos */}
        <div className="border border-gray-100 rounded-xl p-4 mb-3 bg-gray-50/50">
          <p className="text-xs font-semibold text-charcoal-700 mb-3">Miembros activos</p>
          <div className="flex flex-wrap gap-3 items-end">
            <div>
              <label className="block text-[11px] text-charcoal-500 mb-1">Modo</label>
              <select
                value={form.home_members_mode}
                onChange={(e) => setForm({ ...form, home_members_mode: e.target.value })}
                data-testid="settings-home-members-mode"
                className="rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cmic-500/30 focus:border-cmic-500"
              >
                <option value="manual">Manual (número fijo)</option>
                <option value="auto">Automático (cuenta en el directorio)</option>
              </select>
            </div>
            <div>
              <label className="block text-[11px] text-charcoal-500 mb-1">Número manual</label>
              <input
                type="number"
                min={0}
                value={form.home_members_count}
                onChange={(e) => setForm({ ...form, home_members_count: Number(e.target.value || 0) })}
                disabled={form.home_members_mode === 'auto'}
                data-testid="settings-home-members-count"
                className="w-28 rounded-lg border border-gray-200 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-cmic-500/30 focus:border-cmic-500 disabled:opacity-50"
              />
            </div>
          </div>
          <p className="text-[10px] text-charcoal-400 mt-2">
            Modo automático = cuenta intérpretes con perfil visible en el directorio (no incluye suspendidos).
            Modo manual = el número que tú indiques (útil para mostrar miembros suspendidos o un total redondeado).
          </p>
        </div>

        {/* Años de trayectoria */}
        <div className="border border-gray-100 rounded-xl p-4 bg-gray-50/50">
          <p className="text-xs font-semibold text-charcoal-700 mb-3">Años de trayectoria</p>
          <div className="flex flex-wrap gap-3 items-end">
            <div>
              <label className="block text-[11px] text-charcoal-500 mb-1">Modo</label>
              <select
                value={form.home_years_mode}
                onChange={(e) => setForm({ ...form, home_years_mode: e.target.value })}
                data-testid="settings-home-years-mode"
                className="rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cmic-500/30 focus:border-cmic-500"
              >
                <option value="auto">Automático (año actual − fundación)</option>
                <option value="manual">Manual (número fijo)</option>
              </select>
            </div>
            <div>
              <label className="block text-[11px] text-charcoal-500 mb-1">Año de fundación</label>
              <input
                type="number"
                min={1900}
                max={new Date().getFullYear()}
                value={form.home_founding_year}
                onChange={(e) => setForm({ ...form, home_founding_year: Number(e.target.value || 1982) })}
                data-testid="settings-home-founding-year"
                className="w-24 rounded-lg border border-gray-200 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-cmic-500/30 focus:border-cmic-500"
              />
            </div>
            <div>
              <label className="block text-[11px] text-charcoal-500 mb-1">Número manual</label>
              <input
                type="number"
                min={0}
                value={form.home_years_count}
                onChange={(e) => setForm({ ...form, home_years_count: Number(e.target.value || 0) })}
                disabled={form.home_years_mode === 'auto'}
                data-testid="settings-home-years-count"
                className="w-24 rounded-lg border border-gray-200 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-cmic-500/30 focus:border-cmic-500 disabled:opacity-50"
              />
            </div>
          </div>
        </div>

        <div className="mt-4">
          <button
            onClick={save}
            disabled={saving}
            data-testid="settings-home-save-btn"
            className="inline-flex items-center gap-2 bg-cmic-600 text-white rounded-lg px-4 py-2 text-sm font-medium hover:bg-cmic-700 disabled:opacity-50"
          >
            <Save size={14} />{saving ? 'Guardando…' : 'Guardar estadísticas'}
          </button>
        </div>
      </div>
    </div>
  );
}
